import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import { useAuth } from "../../Context/AuthContext";
import ManagementAPI from "./managementAPI";

/**
 * OrgScopedPage — wraps a real tenant page (Intents, Knowledge Base, Flow
 * Builder, …) so a super-admin can manage any organization with the EXACT
 * tenant UI. Renders an org picker; selecting an org opens a full-access
 * impersonation session (see AuthContext) that persists across pages, then
 * mounts the tenant component scoped to that org.
 */
export default function OrgScopedPage({ title, subtitle, children }) {
    const { impersonating, enterImpersonation, exitImpersonation } = useAuth();
    const [orgs, setOrgs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err,  setErr]  = useState("");

    useEffect(() => {
        ManagementAPI.listOrgs(1, "", "")
            .then(d => setOrgs(d.organizations || []))
            .catch(e => setErr(e.message || "Could not load organizations."));
    }, []);

    const pick = async (e) => {
        const id = e.target.value;
        setErr("");
        if (!id) { exitImpersonation(); return; }
        setBusy(true);
        try {
            const data = await ManagementAPI.impersonateOrg(Number(id));
            enterImpersonation(data);
        } catch (e2) {
            setErr(e2.message || "Could not open this organization.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div className="mg-page-head">
                <div>
                    <h1 className="mg-h1">{title}</h1>
                    {subtitle && <p className="mg-sub">{subtitle}</p>}
                </div>
                <div className="mg-orgswitch" style={{ maxWidth: 300 }}>
                    <Building2 size={14} />
                    <select value={impersonating?.id || ""} onChange={pick} disabled={busy}>
                        <option value="">— select organization —</option>
                        {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                </div>
            </div>

            {err && <div className="mg-alert error" style={{ justifyContent: "flex-start" }}>{err}</div>}

            {busy ? (
                <div className="mg-loading">Loading organization…</div>
            ) : impersonating ? (
                <div className="mg-tenant-embed">{children}</div>
            ) : (
                <div className="mg-select-org-prompt">
                    <Building2 size={28} />
                    <strong>Select an organization</strong>
                    <span>
                        Choose an organization above to manage it — exactly like the org's own admin sees it.
                    </span>
                </div>
            )}
        </div>
    );
}
